import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { getSpecificDetails } from "../../service/ApiService";
import { TAX_LIST_DETAIL } from "../../config/ApiConstant";
import { Toast } from "primereact/toast";
import { Section } from "../../components/CommonCompentUi";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import {
  SkeletonbodyTemplate,
  Skeletonitems,
} from "../../appconfig/Settings";
import { FileText } from "lucide-react";
import { Button } from "primereact/button";
import { DocToBeSignedColumns } from "../../appconfig/DatatableSetting";
import Loader from "../../components/Loader/Loader";

interface DocumentToBeSignedProps {
  data?: any;
  loading?: boolean;
  refreshData?: () => void;
}

const DocumentToBeSigned = ({ data, loading, refreshData }: DocumentToBeSignedProps) => {

  // tax payer id
  const { id }: any = useParams();
  const employee_id = id ? atob(id) : null;

  // toast ref
  const toast = useRef<any>(null);

  // tax payer detail obj
  const [userTaxFileData, setUserTaxFileData] = useState<any>(null);

  // document list
  const [documentList, setDocumentList] = useState<any>([]);

  // page load
  const [pageLoad, setPageLoad] = useState<boolean>(false);

  //   filter
  const [globalFieldFilter, setGlobalFieldFilter] = useState<any>(null);


  // scroll position
  const scrollPositionRef = useRef(0);

  // Marital status derived state
  const isMarried = userTaxFileData?.dec_marrige_status === 'Married' || userTaxFileData?.dec_marrige_status === 'Living common-law';

  // Collapsible sections state
  const [expandedSections, setExpandedSections] = useState<any>({
    documents: true,
    spouse_documents: true,
  });



  // useEffect to parse tax payer detail when and data changes
  useEffect(() => {
    if (data && Object.keys(data).length > 0) {
      parseTaxData(data);
    }
  }, [data]);

  // Handle scroll position in tab
  useLayoutEffect(() => {
    window.scrollTo({
      top: scrollPositionRef.current,
      behavior: "auto",
    });
  }, [expandedSections]);

  // get Tax payer detail
  const parseTaxData = (response: any) => {
    const dataToSign = response?.docs_to_sign;
    setUserTaxFileData(response);
    setDocumentList(dataToSign);

    setPageLoad(true);
  };

  // view pdf
  const viewPdf = (rowData: any, field: string) => {
    const url = rowData?.[field];

    if (url) {
      window.open(url, "_blank");
    } else {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "PDF NOT FOUND",
      });
    }
  };

  // action body template
  const actionBodyTemplate = (field: string) => (rowData: any) =>
  (
    <>
      <div className="actions">
        <>
          {rowData?.type ? rowData?.type : ""}
          <Button
            icon="pi pi-file-pdf"
            tooltip="View PDF"
            className="p-button-secondary common-view-pdf-btn ml-3"
            tooltipOptions={{ position: "bottom" }}
            onClick={() => viewPdf(rowData, field)}
          />
        </>
      </div>
    </>
  );

  // view Signed pdf
  const viewPdfSigned = (rowData: any) => {
    const url = rowData?.signed_url;

    if (url) {
      window.open(url, "_blank");
    } else {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "PDF NOT FOUND",
      });
    }
  };

  //   signed pdf body template
  const PdfBodyTemplate = (rowData: any) =>
    rowData.signed_url != null ? (
      <div className="actions">
        <Button
          icon="pi pi-file-pdf"
          tooltip="View PDF"
          className="p-button-secondary common-view-pdf-btn"
          tooltipOptions={{ position: "bottom" }}
          onClick={() => viewPdfSigned(rowData)}
        />
      </div>
    ) : (
      <span className="actions">-</span>
    );

  // serial no template
  const serialNoBodyTemplate = (_: any, options: any) => {
    return options.rowIndex + 1;
  };

  const taxPayerDocs = documentList
    ?.filter((doc: any) => !doc?.is_spouse_sign_doc)
    .sort((a: any, b: any) => (b.signed_url ? 1 : 0) - (a.signed_url ? 1 : 0))
    .filter((doc: any, index: number, self: any[]) =>
      self.findIndex((t: any) => t.type === doc.type) === index
    ) || [];

  const spouseDocs = documentList
    ?.filter((doc: any) => doc?.is_spouse_sign_doc)
    .sort((a: any, b: any) => (b.signed_url ? 1 : 0) - (a.signed_url ? 1 : 0))
    .filter((doc: any, index: number, self: any[]) =>
      self.findIndex((t: any) => t.type === doc.type) === index
    ) || [];

  return (
    <>
      <Toast ref={toast} />

      <div className="tax-payer-modern-container">
        <div className="flex gap-4 w-full">
          {/* Tax Payer Documents */}
          <div className={isMarried ? "w-6" : "w-full"}>
            <Section
              title="Tax Payer Documents To Be Signed"
              icon={FileText}
              sectionKey="documents"
              scrollPositionRef={scrollPositionRef}
              expandedSections={expandedSections}
              setExpandedSections={setExpandedSections}
              className="crud-demo"
            >
              <div className="mt-4">
                {pageLoad ? (
                  <div className="crud-demo">
                    <DataTable
                      value={taxPayerDocs}
                      showGridlines
                      dataKey="id"
                      stripedRows
                      responsiveLayout="scroll"
                      className="datatable-responsive p-datatble-sm "
                      emptyMessage="No Document Found"
                      filters={globalFieldFilter}
                      onFilter={(e) => setGlobalFieldFilter(e.filters)}
                      removableSort
                    >
                      <Column
                        header="S.No"
                        body={serialNoBodyTemplate}
                        headerStyle={{ padding: '7px 10px', fontWeight: 'bold' }}
                        style={{ padding: '7px 10px', width: "80px", textAlign: "center" }}
                      />
                      {DocToBeSignedColumns.map((col) => {
                        if (col.field === "file_url") {
                          return (
                            <Column
                              key={col.field}
                              field={col.field}
                              header={col.header}
                              headerStyle={{ padding: '7px 10px', fontWeight: 'bold' }}
                              style={{ padding: '7px 10px' }}
                              body={actionBodyTemplate(col.field)}
                            />
                          );
                        } else if (col.field === "signed_url") {
                          return (
                            <Column
                              key={col.field}
                              field={col.field}
                              header={col.header}
                              headerStyle={{ padding: '7px 10px', fontWeight: 'bold' }}
                              style={{ padding: '7px 10px' }}
                              body={PdfBodyTemplate}
                            />
                          );
                        }
                        return (
                          <Column
                            key={col.field}
                            field={col.field}
                            header={col.header}
                            headerStyle={{ padding: '7px 10px', fontWeight: 'bold' }}
                            style={{ padding: '7px 10px' }}
                          />
                        );
                      })}
                    </DataTable>
                  </div>
                ) : (
                  <div className="crud-demo">
                    <DataTable value={Skeletonitems}>
                      {DocToBeSignedColumns.map((col) => (
                        <Column key={col.field} field={col.field} header={col.header} body={SkeletonbodyTemplate} />
                      ))}
                    </DataTable>
                  </div>
                )}
              </div>
            </Section>
          </div>

          {/* Spouse Documents */}
          {isMarried && (
            <div className="w-6">
              <Section
                title="Spouse Documents To Be Signed"
                icon={FileText}
                sectionKey="spouse_documents"
                scrollPositionRef={scrollPositionRef}
                expandedSections={expandedSections}
                setExpandedSections={setExpandedSections}
                className="crud-demo"
              >
                <div className="mt-4">
                  {pageLoad ? (
                    <div className="crud-demo">
                      <DataTable
                        value={spouseDocs}
                        showGridlines
                        dataKey="id"
                        stripedRows
                        responsiveLayout="scroll"
                        className="datatable-responsive p-datatble-sm "
                        emptyMessage="No Document Found"
                        filters={globalFieldFilter}
                        onFilter={(e) => setGlobalFieldFilter(e.filters)}
                        removableSort
                      >
                        <Column
                          header="S.No"
                          body={serialNoBodyTemplate}
                          headerStyle={{ padding: '7px 10px', fontWeight: 'bold' }}
                          style={{ padding: '7px 10px', width: "80px", textAlign: "center" }}
                        />
                        {DocToBeSignedColumns.map((col) => {
                          if (col.field === "file_url") {
                            return (
                              <Column
                                key={col.field}
                                field={col.field}
                                header={col.header}
                                headerStyle={{ padding: '7px 10px', fontWeight: 'bold' }}
                                style={{ padding: '7px 10px' }}
                                body={actionBodyTemplate(col.field)}
                              />
                            );
                          } else if (col.field === "signed_url") {
                            return (
                              <Column
                                key={col.field}
                                field={col.field}
                                header={col.header}
                                headerStyle={{ padding: '7px 10px', fontWeight: 'bold' }}
                                style={{ padding: '7px 10px' }}
                                body={PdfBodyTemplate}
                              />
                            );
                          }
                          return (
                            <Column
                              key={col.field}
                              field={col.field}
                              header={col.header}
                              headerStyle={{ padding: '7px 10px', fontWeight: 'bold' }}
                              style={{ padding: '7px 10px' }}
                            />
                          );
                        })}
                      </DataTable>
                    </div>
                  ) : (
                    <div className="crud-demo">
                      <DataTable value={Skeletonitems}>
                        {DocToBeSignedColumns.map((col) => (
                          <Column key={col.field} field={col.field} header={col.header} body={SkeletonbodyTemplate} />
                        ))}
                      </DataTable>
                    </div>
                  )}
                </div>
              </Section>
            </div>
          )}
        </div>
      </div>

      {/* {pageLoad === false && <Loader />} */}
    </>
  );
};

export default DocumentToBeSigned;
